export interface LocatedOffer {
  location?: { name: string; precision?: string; coordinates?: [number, number] } | null;
}

/** Separate clickable symbols in screen pixels only; leave geographic coordinates unchanged. */
export function individualLocatedOffers<T extends LocatedOffer>(offers: T[]) {
  const located = offers.filter((offer) => {
    const c = offer.location?.coordinates;
    return c?.length === 2 && c.every(Number.isFinite) && offer.location?.precision !== "city";
  });
  return located.map((offer) => {
    const c = offer.location?.coordinates;
    const siblings = located.filter((other) => other.location?.coordinates?.[0] === c?.[0] && other.location?.coordinates?.[1] === c?.[1]);
    const index = siblings.indexOf(offer);
    const angle = index * Math.PI * 2 / siblings.length;
    const radius = siblings.length > 1 ? Math.max(22, siblings.length * 5) : 0;
    return { offer, offset: [Math.cos(angle) * radius, Math.sin(angle) * radius] as [number, number] };
  });
}

/** Preserve every returned offer, including offers without map coordinates. */
export function interleaveOffers<T>(buckets: T[][]): T[] {
  const result: T[] = [];
  const length = Math.max(0, ...buckets.map((bucket) => bucket.length));
  for (let index = 0; index < length; index++) {
    for (const bucket of buckets) {
      if (index < bucket.length) result.push(bucket[index]);
    }
  }
  return result;
}

/** A city marker exposes every offer at that location, rather than hiding them under one another. */
export function groupLocatedOffers<T extends LocatedOffer>(offers: T[]) {
  const groups = new Map<string, { name: string; coordinates: [number, number]; offers: T[] }>();
  for (const offer of offers) {
    const location = offer.location;
    const coordinates = location?.coordinates;
    if (!location || !coordinates || !coordinates.every(Number.isFinite)) continue;
    const key = location.name.trim().toLocaleLowerCase("de");
    const group = groups.get(key) ?? Array.from(groups.values()).find((candidate) =>
      Math.abs(candidate.coordinates[0] - coordinates[0]) < 0.25 &&
      Math.abs(candidate.coordinates[1] - coordinates[1]) < 0.15,
    );
    if (group) {
      group.offers.push(offer);
      if (!group.name.split(" / ").includes(location.name)) group.name += ` / ${location.name}`;
    }
    else groups.set(key, { name: location.name, coordinates, offers: [offer] });
  }
  return Array.from(groups.values());
}